import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Tabs, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography, Chip, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, CircularProgress, Alert
} from '@mui/material';
import { CheckCircle as ApproveIcon, Cancel as RejectIcon, Redo as RevisionIcon } from '@mui/icons-material';
import { contentService } from '../services/api';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const parseCasting = (castingList) => {
  if (!castingList) return '—';
  try {
    const arr = typeof castingList === 'string' ? JSON.parse(castingList) : castingList;
    return Array.isArray(arr) ? arr.join(', ') : castingList;
  } catch { return castingList; }
};

const getStatusColor = (status) => {
  const map = {
    'Content Ready': 'info',
    'Sent for Revision': 'error',
    'Editing Completed': 'secondary',
    'Content Approved': 'warning',
  };
  return map[status] || 'default';
};

function ContentTable({ rows, loading, onApprove, onReject, onSendRevision, showActions }) {
  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  if (!rows.length) return <Alert severity="info" sx={{ m: 2 }}>No content found.</Alert>;
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            <TableCell><strong>Title</strong></TableCell>
            <TableCell><strong>Channel</strong></TableCell>
            <TableCell><strong>Type</strong></TableCell>
            <TableCell><strong>Shoot Date</strong></TableCell>
            <TableCell><strong>Status</strong></TableCell>
            <TableCell><strong>Casting</strong></TableCell>
            <TableCell><strong>Editor</strong></TableCell>
            {showActions && <TableCell align="center"><strong>Actions</strong></TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(c => (
            <TableRow key={c.id} hover>
              <TableCell>
                <Typography variant="body2" fontWeight={500}>{c.contentTitle || c.title || '—'}</Typography>
                {c.contentId && <Typography variant="caption" color="text.secondary">{c.contentId}</Typography>}
              </TableCell>
              <TableCell><Chip label={c.channel || '—'} size="small" variant="outlined" /></TableCell>
              <TableCell>{c.contentType || '—'}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(c.shootDate)}</TableCell>
              <TableCell>
                <Chip label={c.status} size="small" color={getStatusColor(c.status)} />
              </TableCell>
              <TableCell>{parseCasting(c.castingList)}</TableCell>
              <TableCell>{c.editor || '—'}</TableCell>
              {showActions && (
                <TableCell align="center">
                  <Box display="flex" gap={1} justifyContent="center" flexWrap="wrap">
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<ApproveIcon />}
                      onClick={() => onApprove(c)}
                      sx={{ textTransform: 'none' }}
                    >
                      Approve
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      startIcon={<RevisionIcon />}
                      onClick={() => onSendRevision(c)}
                      sx={{ textTransform: 'none' }}
                    >
                      Send for Revision
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<RejectIcon />}
                      onClick={() => onReject(c)}
                      sx={{ textTransform: 'none' }}
                    >
                      Reject
                    </Button>
                  </Box>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function ContentApprovalTab() {
  const [subTab, setSubTab] = useState(0);
  const [forApproval, setForApproval] = useState([]);
  const [forRelease, setForRelease] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectDialog, setRejectDialog] = useState({ open: false, content: null });
  const [revisionDialog, setRevisionDialog] = useState({ open: false, content: null });
  const [rejectRemark, setRejectRemark] = useState('');
  const [revisionRemark, setRevisionRemark] = useState('');
  const [remarkError, setRemarkError] = useState('');
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [approvalRes, revisionRes, releaseRes] = await Promise.all([
        contentService.getAll({ status: 'Content Ready', limit: 200 }),
        contentService.getAll({ status: 'Sent for Revision', limit: 200 }),
        contentService.getAll({ status: 'Editing Completed', limit: 200 }),
      ]);
      setForApproval([
        ...(approvalRes.data.data?.content || []),
        ...(revisionRes.data.data?.content || []),
      ]);
      setForRelease(releaseRes.data.data?.content || []);
    } catch (e) {
      console.error('Error fetching content for approval:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApprove = async (content) => {
    try {
      await contentService.update(content.id, { status: 'Content Approved' });
      setSnack(`"${content.contentTitle}" approved.`);
      fetchData();
    } catch (e) {
      setSnack('Failed to approve. Please try again.');
    }
  };

  const openRevisionDialog = (content) => {
    setRevisionDialog({ open: true, content });
    setRevisionRemark('');
    setRemarkError('');
  };

  const handleSendRevision = async () => {
    if (!revisionRemark.trim()) {
      setRemarkError('Please provide revision notes for the editor.');
      return;
    }
    setSaving(true);
    try {
      await contentService.update(revisionDialog.content.id, {
        status: 'Sent for Revision',
        remarks: revisionRemark.trim()
      });
      setRevisionDialog({ open: false, content: null });
      setSnack(`"${revisionDialog.content.contentTitle}" sent for revision.`);
      fetchData();
    } catch (e) {
      setSnack('Failed to send for revision. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const openRejectDialog = (content) => {
    setRejectDialog({ open: true, content });
    setRejectRemark('');
    setRemarkError('');
  };

  const handleReject = async () => {
    if (!rejectRemark.trim()) {
      setRemarkError('Remark is required for rejection.');
      return;
    }
    setSaving(true);
    try {
      await contentService.update(rejectDialog.content.id, { remarks: rejectRemark.trim() });
      setRejectDialog({ open: false, content: null });
      setSnack(`"${rejectDialog.content.contentTitle}" rejected with remark.`);
      fetchData();
    } catch (e) {
      setSnack('Failed to reject. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        <Tab label={`Content for Approval (${forApproval.length})`} />
        <Tab label={`Content for Release (${forRelease.length})`} />
      </Tabs>

      {snack && (
        <Alert severity="success" sx={{ m: 2 }} onClose={() => setSnack('')}>{snack}</Alert>
      )}

      {subTab === 0 && (
        <ContentTable
          rows={forApproval}
          loading={loading}
          onApprove={handleApprove}
          onReject={openRejectDialog}
          onSendRevision={openRevisionDialog}
          showActions
        />
      )}

      {subTab === 1 && (
        <ContentTable
          rows={forRelease}
          loading={loading}
          showActions={false}
        />
      )}

      {/* Send for Revision Dialog */}
      <Dialog open={revisionDialog.open} onClose={() => setRevisionDialog({ open: false, content: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Send for Revision</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Sending back: <strong>{revisionDialog.content?.contentTitle}</strong>
          </Typography>
          <TextField
            label="Revision Notes *"
            multiline
            rows={3}
            fullWidth
            value={revisionRemark}
            onChange={(e) => { setRevisionRemark(e.target.value); setRemarkError(''); }}
            error={!!remarkError}
            helperText={remarkError || 'Describe what needs to be fixed or changed.'}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevisionDialog({ open: false, content: null })} disabled={saving}>Cancel</Button>
          <Button onClick={handleSendRevision} color="warning" variant="contained" disabled={saving}>
            {saving ? 'Sending...' : 'Send for Revision'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, content: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Content</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Rejecting: <strong>{rejectDialog.content?.contentTitle}</strong>
          </Typography>
          <TextField
            label="Rejection Remark *"
            multiline
            rows={3}
            fullWidth
            value={rejectRemark}
            onChange={(e) => { setRejectRemark(e.target.value); setRemarkError(''); }}
            error={!!remarkError}
            helperText={remarkError || 'Explain why this content is being rejected.'}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, content: null })} disabled={saving}>Cancel</Button>
          <Button onClick={handleReject} color="error" variant="contained" disabled={saving}>
            {saving ? 'Rejecting...' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
